import { useEffect, useRef, useMemo } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  IconExpand,
  useQueryState,
  useScroll,
  useFilters,
  Table,
  IconLoader,
  useGetList,
  SchemaFilterType,
  PermissionDenied,
  usePrevious,
  PermissionEnum,
  BaseellaStatusTitle,
  SelectItemProps,
  formatBalance,
  Currency,
  PaymentType as PaymentTypeEnum,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetProcessingRulesQuery,
  useLazyGetProcessingRulesPspPartnersQuery,
  useLazyGetProcessingRulesPspIntegrationsQuery,
} from './store/service';
import {
  columns,
  schema,
  columnsLabels,
  filtersSchema,
} from './variables/list';
import { setTakeCount } from './store/slice';
import { CurrencyTypeEnum, ProcessingRule } from './types';
import {
  useLazyGetEmployeesTransactionsTypesQuery,
  useLazyGetEmployeesCASPQuery,
} from '../../settings/store/services/transactions-types';
import ROUTES from '../../../routes';
import { CASP, PaymentType, TransactionType } from '../../settings/types';

import cn from '../../settings/styles.module.css';

const formatProcessingRulesList = (
  list: ProcessingRule[],
  currencies?: Currency[],
  pspPartnes?: SelectItemProps[],
  pspIntegrations?: SelectItemProps[],
  transactionsTypes?: TransactionType[],
  paymentTypes?: PaymentType[],
  casp?: CASP[],
): ProcessingRule[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {};

    let currencyValue;
    let pspPartnerValue;
    let pspIntegrationValue;

    if (result?.currencyId) {
      currencyValue = currencies?.find(
        (currency) => currency.id === result.currencyId,
      )?.name;
    } else if (
      !result.currencyId &&
      result.currencyType === CurrencyTypeEnum.ALL
    ) {
      currencyValue = 'All currencies';
    } else {
      currencyValue = '-';
    }

    if (result?.pspPartnerId) {
      pspPartnerValue = pspPartnes?.find(
        (partner) => partner.value === result.pspPartnerId,
      )?.name;
    } else if (
      !result.pspPartnerId &&
      result.pspPartnerType === CurrencyTypeEnum.ALL
    ) {
      pspPartnerValue = 'All partners';
    } else {
      pspPartnerValue = '-';
    }

    if (result?.pspIntegrationId) {
      pspIntegrationValue = pspIntegrations
        ?.find((integration) => integration.value === result.pspIntegrationId)
        ?.name?.split('_')
        .map((name) => `${name.charAt(0)}${name.slice(1).toLowerCase()}`)
        .join(' ');
    } else if (
      !result.pspIntegrationId &&
      result.pspIntegrationType === CurrencyTypeEnum.ALL
    ) {
      pspIntegrationValue = 'All types';
    } else {
      pspIntegrationValue = '-';
    }

    const currentPaymentType = paymentTypes?.find(
      (paymentType) => paymentType.id === result.paymentCategoryId,
    );

    const availableCurrencies: SelectItemProps[] = [];

    if (currentPaymentType) {
      if (
        currentPaymentType.paymentType !== PaymentTypeEnum.CRYPTO_ASSET_PAYMENT
      ) {
        currentPaymentType?.currencies?.edges?.forEach((currency) =>
          availableCurrencies.push({
            name: currency.node.name,
            value: currency.node.id,
          }),
        );
      } else if (
        currentPaymentType.paymentType ===
          PaymentTypeEnum.CRYPTO_ASSET_PAYMENT &&
        result.caspId &&
        casp
      ) {
        const currentCasp = casp?.find(
          (caspValue) => caspValue.id === result.caspId,
        );

        if (currentCasp) {
          currentCasp?.currencies?.forEach((currency) =>
            availableCurrencies.push({
              name: currency.currency.name,
              value: currency.currencyId,
            }),
          );
        }
      }
    }

    render.active = (
      <div className={cn.processingRuleStatusWrapper}>
        <div
          className={`${cn.processingRuleStatus} ${
            result.active ? cn.activeStatus : ''
          }`}
        >
          {result.active ? 'Active' : 'Inactive'}
        </div>
      </div>
    );
    render.typeId = (
      <div>
        {result?.typeId
          ? transactionsTypes
              ?.find((type) => type.id === result?.typeId)
              ?.name?.split('_')
              .map((type) => `${type.charAt(0)}${type.slice(1).toLowerCase()}`)
              .join(' ')
          : 'All types'}
      </div>
    );
    render.fromStatus = (
      <div>
        {result.fromStatus ? BaseellaStatusTitle[result.fromStatus] : '-'}
      </div>
    );
    render.toStatus = (
      <div>{result.toStatus ? BaseellaStatusTitle[result.toStatus] : '-'}</div>
    );
    render.currenciesIds = (
      <div className={!result?.currenciesIds?.length ? cn.emptyValue : ''}>
        {result.currenciesIds && result.currenciesIds?.length > 0
          ? result?.currenciesIds
              ?.map(
                (id) =>
                  availableCurrencies?.find((currency) => currency.value === id)
                    ?.name,
              )
              ?.join(', ')
          : 'All currencies'}
      </div>
    );

    render.processingType = (
      <div>
        {result?.processingType
          ? result?.processingType
              ?.split('_')
              ?.map((type) => `${type.charAt(0)}${type.slice(1).toLowerCase()}`)
              .join(' ')
          : '-'}
      </div>
    );
    render.currencyId = (
      <div
        className={
          !result.currencyId && result.currencyType === CurrencyTypeEnum.ALL
            ? cn.emptyValue
            : ''
        }
      >
        {currencyValue}
      </div>
    );

    render.pspPartnerId = (
      <div
        className={
          !result.pspPartnerId && result.pspPartnerType === CurrencyTypeEnum.ALL
            ? cn.emptyValue
            : ''
        }
      >
        {pspPartnerValue}
      </div>
    );

    render.pspIntegrationId = (
      <div
        className={
          !result.pspIntegrationId &&
          result.pspIntegrationType === CurrencyTypeEnum.ALL
            ? cn.emptyValue
            : ''
        }
      >
        {pspIntegrationValue}
      </div>
    );

    render.amount = (
      <div>
        {result?.amount
          ? formatBalance(
              result.amount,
              currencies?.find((currency) => currency.id === result.currencyId),
            )
          : '-'}
      </div>
    );

    result.render = render;

    return result;
  });

  return nextList;
};

const ProcessingRulesList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    settings: {
      processingRules: {
        processingRules,
        pagination,
        takeCount,
        pspPartnes,
        pspIntegrations,
        errorsList,
      },
      paymentTypes: { paymentTypes },
      transactionsTypes: { values: transactionsTypes, casp },
    },
    administrationDictionaries: { activePermissions, activeCurrencies },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const [getProcessingRules, { isLoading, isFetching }] =
    useLazyGetProcessingRulesQuery();

  const [getPspPartners] = useLazyGetProcessingRulesPspPartnersQuery();
  const [getPspIntegrations] = useLazyGetProcessingRulesPspIntegrationsQuery();
  const [getTransactionTypes] = useLazyGetEmployeesTransactionsTypesQuery();
  const [getCASP] = useLazyGetEmployeesCASPQuery();

  const { getListAction, onSort, onFilter, setPage, errorList } =
    useGetList<ProcessingRule>({
      getList: getProcessingRules,
      onFilterChange,
      onSortChange,
      schema,
    });

  const { search } = useLocation();

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatProcessingRulesList(
      processingRules as ProcessingRule[],
      activeCurrencies,
      pspPartnes,
      pspIntegrations,
      transactionsTypes,
      paymentTypes,
      casp,
    );

    return data;
  }, [
    processingRules,
    activeCurrencies,
    filters,
    sort,
    pspPartnes,
    pspIntegrations,
    transactionsTypes,
    paymentTypes,
    casp,
  ]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (!transactionsTypes) {
      getTransactionTypes();
    }
  }, [transactionsTypes]);

  useEffect(() => {
    if (transactionsTypes) {
      setValues(
        'typeId',
        transactionsTypes
          .filter((item) => item.name !== 'INTERAL_PAYMENT')
          ?.map((item) => ({
            name: item.name
              .split('_')
              .map((name) => `${name.charAt(0)}${name.slice(1).toLowerCase()}`)
              .join(' '),
            value: item.id,
          })),
      );
    }
  }, [transactionsTypes]);

  useEffect(() => {
    if (pspPartnes) {
      setValues('pspPartnerId', pspPartnes);
    }

    if (!pspPartnes) {
      getPspPartners();
    }
  }, [pspPartnes]);

  useEffect(() => {
    if (!casp) {
      getCASP();
    }
  }, [casp]);

  useEffect(() => {
    if (pspIntegrations) {
      setValues(
        'pspIntegrationId',
        pspIntegrations?.map((item) => ({
          name: item.name
            .split('_')
            .map((name) => `${name.charAt(0)}${name.slice(1).toLowerCase()}`)
            .join(' '),
          value: item.value,
        })),
      );
    }

    if (!pspIntegrations) {
      getPspIntegrations();
    }
  }, [pspIntegrations]);

  useEffect(() => {
    if (activeCurrencies) {
      setValues(
        'currencyId',
        activeCurrencies?.map((item) => ({ name: item.name, value: item.id })),
      );
    }
  }, [activeCurrencies]);

  useEffect(() => {
    if (
      takeCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !processingRules &&
      sort &&
      Object.keys(sort).length &&
      activePermissions?.includes(PermissionEnum.TRANSACTIONS_RULES_LIST)
    ) {
      getListAction({
        filters: {
          ...filters,
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
        },
        sort,
      });
    }
  }, [filters, sort, processingRules, takeCount, activePermissions]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 47;

        dispatch(setTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.processingRulesLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !activePermissions?.includes(PermissionEnum.TRANSACTIONS_RULES_LIST)
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <div className={cn.processingRulesTableWrapper}>
        <Table
          columns={columns}
          data={formattedData || []}
          onSort={(name) => onSort(name)}
          filters={filters}
          sort={sort}
          columnsLabels={columnsLabels}
          onFilterChange={(filterValues) => onFilter(filterValues)}
          filtersComponent={filtersComponent}
          isLoading={isFetching || takeCount === 0 || isLoading}
          handleSubmit={handleSubmit}
          pagination={pagination}
          setPage={(data) => setPage({ ...data, filters })}
          take={takeCount.toString()}
          error={errorsList || errorList}
          tableTopClassName={cn.processingRulesTableTop}
          topContent={
            <div>
              {activePermissions?.includes(
                PermissionEnum.TRANSACTIONS_RULES_CREATE_ACTION,
              ) && (
                <NavLink
                  className={cn.createRuleLink}
                  to={`${ROUTES.TRANSACTION_SETTINGS_PROCESSING_RULES_CREATE}${
                    search || ''
                  }`}
                >
                  <IconExpand width={16} height={16} />
                  Add rule
                </NavLink>
              )}
            </div>
          }
          withScroll
          withVerticalScroll
          clickable
        />
      </div>
    );
  }

  return (
    <div className={cn.processingRulesList} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default ProcessingRulesList;
